import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Iconify from 'src/components/Iconify';
import { Button } from 'src/@/components/ui/button';
import { Input } from 'src/@/components/ui/input';
import { Card } from 'src/@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'src/@/components/ui/dropdown-menu';
import PageContainerHeader from 'src/components/PageContainerHeader';
import GenericTable from 'src/components/table/GenericTable';
import { GENERIC_TABLE_DESIGN } from 'src/components/table/GenericTable.design';
import propostasService from 'src/services/propostasService';
import useAuth from 'src/hooks/useAuth';
import { useToast } from 'src/@/components/ui/use-toast';
import { getListaUsuariosByClienteId } from 'src/redux/slices/user';
import ModelosList from './ModelosList';
import RelatoriosPropostas from './RelatoriosPropostas';
import CompartilharPropostaDialog from './CompartilharPropostaDialog';
import AcompanhamentoDrawer from './AcompanhamentoDrawer';
import KanbanBoard from './KanbanBoard';
import { PROPOSTAS_DESIGN } from './Propostas.design';
import { caminhoEdicao, iconeTipo, isOrcamento, ROTA_NOVO_ORCAMENTO, ROTA_NOVA_PROPOSTA } from './propostasRotas';

const TABS = [
  { key: 'propostas', label: 'Propostas', icon: 'mdi:file-document-outline' },
  { key: 'modelos', label: 'Modelos', icon: 'mdi:file-multiple-outline' },
  { key: 'relatorios', label: 'Relatórios', icon: 'mdi:chart-line' }
];

const HEAD_LABELS = [
  { id: 'titulo', label: 'Documento', className: 'flex-1 min-w-[220px] pr-4' },
  { id: 'status', label: 'Status', className: 'w-[140px]' },
  { id: 'lead', label: 'Oportunidade', className: 'w-[180px]' },
  { id: 'vendedor', label: 'Vendedor', className: 'w-[160px]' },
  { id: 'valor', label: 'Valor', className: 'w-[140px] text-right' },
  { id: 'data', label: 'Criada em', className: 'w-[140px]' },
  { id: 'acoes', label: '', className: 'w-[80px]' },
];

function formatCurrency(value) {
  if (value === undefined || value === null) return '-';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

function formatDate(dateString) {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('pt-BR');
}

// Item do menu "Nova Proposta": ícone em chip, título, descrição de uma linha e
// seta que aparece no hover (afirma que o item leva a algum lugar). `destaque`
// pinta o chip na cor do produto; `badge` marca o item (ex.: "IA").
function OpcaoNovaProposta({ icone, titulo, descricao, badge, destaque, onClick }) {
  return (
    <DropdownMenuItem
      className="cursor-pointer rounded-lg px-1.5 py-1.5 focus:bg-violet-50/70 dark:focus:bg-violet-500/10 group"
      onClick={onClick}
    >
      <div className="flex items-center gap-2.5 w-full min-w-0">
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
          destaque
            ? 'bg-violet-100/70 dark:bg-violet-500/15 text-[#7C3AED]'
            : 'bg-gray-100 dark:bg-gray-500/10 text-gray-500 dark:text-gray-400 group-hover:bg-violet-100/70 group-hover:text-[#7C3AED] dark:group-hover:bg-violet-500/15'
        }`}>
          <Iconify icon={icone} width={14} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-medium text-gray-900 dark:text-[#D1D2D3] leading-tight flex items-center gap-1.5">
            {titulo}
            {badge && (
              <span className="px-1 py-px rounded bg-[#7C3AED] text-white text-[8px] font-bold uppercase tracking-wider leading-normal">
                {badge}
              </span>
            )}
          </p>
          <p className="text-[10px] text-gray-400 leading-tight mt-0.5 truncate">{descricao}</p>
        </div>
        <Iconify
          icon="eva:arrow-ios-forward-fill"
          width={13}
          className="text-gray-300 dark:text-gray-600 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
        />
      </div>
    </DropdownMenuItem>
  );
}

export default function Propostas() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const { usuariosList } = useSelector((state) => state.user);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'propostas';

  const [propostas, setPropostas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterVendedor, setFilterVendedor] = useState('all');
  const [filterTipo, setFilterTipo] = useState('all');
  const [shareTarget, setShareTarget] = useState(null);
  const [acompanhamentoTarget, setAcompanhamentoTarget] = useState(null);
  const [viewMode, setViewMode] = useState('kanban');
  const [estagios, setEstagios] = useState([]);

  useEffect(() => {
    if (user?.clienteId) {
      if (usuariosList.length === 0) dispatch(getListaUsuariosByClienteId(user.clienteId));
      loadPropostas();
      loadEstagios();
    }
  }, [user?.clienteId]);

  const loadEstagios = async () => {
    try {
      const response = await propostasService.buscarConfig(user?.clienteId);
      if (!response.error) setEstagios(response.data?.estagios || []);
    } catch (error) {
      console.error('Erro ao carregar estágios de propostas:', error);
    }
  };

  const handleStatusChange = async (propostaId, novoStatus) => {
    let statusAnterior;
    setPropostas(prev => prev.map(p => {
      if (p._id !== propostaId) return p;
      statusAnterior = p.status;
      return { ...p, status: novoStatus };
    }));
    try {
      const response = await propostasService.atualizarStatus(propostaId, novoStatus);
      if (response.error) throw new Error(response.message);
      if (response.data) setPropostas(prev => prev.map(p => p._id === propostaId ? response.data : p));
    } catch (error) {
      // desfaz a mudança otimista
      if (statusAnterior !== undefined) {
        setPropostas(prev => prev.map(p => p._id === propostaId ? { ...p, status: statusAnterior } : p));
      }
      toast({ title: 'Erro', description: error.message || 'Não foi possível mover a proposta', variant: 'destructive' });
    }
  };

  const loadPropostas = async () => {
    try {
      setLoading(true);
      const response = await propostasService.listar(user?.clienteId);
      if (!response.error) setPropostas(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar propostas:', error);
      toast({ title: 'Erro', description: 'Não foi possível carregar as propostas', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const filteredPropostas = useMemo(() => {
    let result = propostas;
    const trimmed = searchTerm.trim().toLowerCase();
    if (trimmed) result = result.filter(p => p.titulo?.toLowerCase().includes(trimmed));
    if (filterStatus !== 'all') result = result.filter(p => p.status === filterStatus);
    if (filterVendedor !== 'all') result = result.filter(p => (p.vendedor_id?._id || p.vendedor_id) === filterVendedor);
    // Documentos anteriores ao campo `tipo` contam como proposta.
    if (filterTipo === 'orcamento') result = result.filter(isOrcamento);
    else if (filterTipo === 'proposta') result = result.filter(p => !isOrcamento(p));
    return result;
  }, [propostas, searchTerm, filterStatus, filterVendedor, filterTipo]);

  const renderStatusBadge = (status) => {
    const cfg = PROPOSTAS_DESIGN.status[status] || PROPOSTAS_DESIGN.status.rascunho;
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider rounded-md ${cfg.bg} ${cfg.text}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
        {cfg.label}
      </span>
    );
  };

  return (
    <div className={PROPOSTAS_DESIGN.page.container}>
      <PageContainerHeader
        titleSection={
          <div className="flex items-center gap-2 min-w-fit">
            <div className={PROPOSTAS_DESIGN.page.iconBox}>
              <Iconify icon="mdi:file-document-outline" className={PROPOSTAS_DESIGN.page.icon} width={20} />
            </div>
            <div>
              <h3 className={PROPOSTAS_DESIGN.page.title}>Propostas</h3>
            </div>
          </div>
        }
        middleSection={
          <div className="flex flex-wrap items-end gap-2 flex-1">
            <div className="w-full sm:w-[150px]">
              <span className="text-[10px] font-semibold text-gray-500 dark:text-[#ABABAD] uppercase tracking-wider mb-1 block pl-1">Tipo</span>
              <Select value={filterTipo} onValueChange={setFilterTipo}>
                <SelectTrigger className="h-9 text-xs border-gray-200 dark:border-[#383838] bg-white dark:bg-[#222529] text-gray-900 dark:text-[#D1D2D3] focus:ring-[#7C3AED]/20">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="orcamento">Orçamentos</SelectItem>
                  <SelectItem value="proposta">Propostas</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="w-full sm:w-[160px]">
              <span className="text-[10px] font-semibold text-gray-500 dark:text-[#ABABAD] uppercase tracking-wider mb-1 block pl-1">Status</span>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="h-9 text-xs border-gray-200 dark:border-[#383838] bg-white dark:bg-[#222529] text-gray-900 dark:text-[#D1D2D3] focus:ring-[#7C3AED]/20">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {Object.entries(PROPOSTAS_DESIGN.status).map(([key, cfg]) => (
                    <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {['admin', 'root', 'supervisor'].includes(user?.level) && (
              <div className="w-full sm:w-[180px]">
                <span className="text-[10px] font-semibold text-gray-500 dark:text-[#ABABAD] uppercase tracking-wider mb-1 block pl-1">Vendedor</span>
                <Select value={filterVendedor} onValueChange={setFilterVendedor}>
                  <SelectTrigger className="h-9 text-xs border-gray-200 dark:border-[#383838] bg-white dark:bg-[#222529] text-gray-900 dark:text-[#D1D2D3] focus:ring-[#7C3AED]/20">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {usuariosList.map(u => (
                      <SelectItem key={u._id} value={u._id}>{u.nomeFantasia || u.nome || u.email}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        }
        rightSection={
          <div className="flex items-center gap-2 w-full md:w-auto justify-end">
            <div className="relative w-full sm:w-[200px]">
              <Input
                className="w-full pl-3 pr-10 h-9 bg-gray-50 dark:bg-[#1A1D21] border-gray-200 dark:border-[#383838] focus:border-[#7C3AED] focus:ring-[#7C3AED]/20 text-sm rounded-lg dark:text-[#D1D2D3]"
                placeholder="Buscar propostas..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              <Iconify icon="eva:search-outline" width={18} className="absolute inset-y-0 right-3 my-auto text-gray-400" />
            </div>
            {activeTab === 'propostas' && (
              <div className="flex items-center bg-gray-50 dark:bg-[#1A1D21] rounded-lg border border-gray-200 dark:border-[#383838] p-0.5">
                <button
                  onClick={() => setViewMode('kanban')}
                  title="Visualizar em kanban"
                  className={`px-2 py-1 rounded-md text-xs font-medium transition-all ${viewMode === 'kanban' ? 'bg-[#7C3AED]/10 text-[#7C3AED]' : 'text-gray-500 dark:text-[#ABABAD] hover:text-gray-700 dark:hover:text-[#D1D2D3]'}`}
                >
                  <Iconify icon="mdi:view-column-outline" width={16} />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  title="Visualizar em lista"
                  className={`px-2 py-1 rounded-md text-xs font-medium transition-all ${viewMode === 'list' ? 'bg-[#7C3AED]/10 text-[#7C3AED]' : 'text-gray-500 dark:text-[#ABABAD] hover:text-gray-700 dark:hover:text-[#D1D2D3]'}`}
                >
                  <Iconify icon="mdi:format-list-bulleted" width={16} />
                </button>
              </div>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className={PROPOSTAS_DESIGN.page.actionButton}>
                  <Iconify icon="eva:plus-fill" className="mr-2" width={16} />
                  Criar
                  <Iconify icon="eva:chevron-down-fill" className="ml-1.5" width={14} />
                </Button>
              </DropdownMenuTrigger>
              {/* 268px: largura suficiente para as descrições caberem em UMA linha
                  (a 220px quebravam em 2/3 linhas e desalinhavam o menu). */}
              <DropdownMenuContent align="end" className="w-[268px] p-1">
                <DropdownMenuLabel className="px-1.5 pt-1 pb-1 text-[9px] font-bold uppercase tracking-wider text-gray-400">
                  Documento
                </DropdownMenuLabel>
                <OpcaoNovaProposta
                  icone="mdi:receipt-text-outline"
                  destaque
                  titulo="Orçamento"
                  descricao="Formulário rápido com prévia do documento"
                  onClick={() => navigate(ROTA_NOVO_ORCAMENTO)}
                />
                <OpcaoNovaProposta
                  icone="mdi:file-document-edit-outline"
                  titulo="Proposta comercial"
                  descricao="Editor completo, com design livre"
                  onClick={() => navigate(`${ROTA_NOVA_PROPOSTA}?modo=canvas`)}
                />

                <DropdownMenuSeparator className="my-1" />

                <DropdownMenuLabel className="px-1.5 pt-0.5 pb-1 text-[9px] font-bold uppercase tracking-wider text-gray-400">
                  Começar pronto
                </DropdownMenuLabel>
                <OpcaoNovaProposta
                  icone="mdi:creation"
                  destaque
                  badge="IA"
                  titulo="Criar com IA"
                  descricao="A partir de uma conversa ou instrução"
                  onClick={() => navigate('/dashboard/propostas/nova?modo=canvas&ia=1')}
                />
                <OpcaoNovaProposta
                  icone="mdi:file-multiple-outline"
                  titulo="A partir de um modelo"
                  descricao="Escolher um modelo pronto ou seu"
                  onClick={() => setSearchParams({ tab: 'modelos' })}
                />
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        }
      />

      <div className={PROPOSTAS_DESIGN.page.content}>
        <div className="flex items-center gap-1 mb-4 border-b border-gray-100 dark:border-[#383838]">
          {TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setSearchParams(tab.key === 'propostas' ? {} : { tab: tab.key })}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.key ? 'border-[#7C3AED] text-[#7C3AED]' : 'border-transparent text-gray-500 dark:text-[#ABABAD] hover:text-gray-700 dark:hover:text-[#D1D2D3]'}`}
            >
              <Iconify icon={tab.icon} width={16} />
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'modelos' && <ModelosList />}
        {activeTab === 'relatorios' && <RelatoriosPropostas />}

        {activeTab === 'propostas' && (
        <>
        {loading ? (
          <Card className="rounded-2xl shadow-sm border border-gray-100 dark:border-[#383838] dark:bg-[#222529] overflow-hidden">
            <div className="flex flex-col items-center justify-center h-64 text-gray-400 dark:text-[#ABABAD]">
              <Iconify icon="mdi:loading" className="w-8 h-8 animate-spin mb-4 text-[#7C3AED]" />
              <p className="text-sm font-medium">Carregando propostas...</p>
            </div>
          </Card>
        ) : viewMode === 'kanban' ? (
          <KanbanBoard
            propostas={filteredPropostas}
            estagios={estagios}
            onStatusChange={handleStatusChange}
            onShare={setShareTarget}
            onAcompanhar={setAcompanhamentoTarget}
          />
        ) : filteredPropostas.length === 0 ? (
          <Card className="rounded-2xl shadow-sm border border-gray-100 dark:border-[#383838] dark:bg-[#222529] overflow-hidden">
            <div className="flex flex-col items-center justify-center p-12 text-center">
              <div className="w-16 h-16 bg-gray-50 dark:bg-[#1A1D21] rounded-full flex items-center justify-center mb-4 text-gray-400 dark:text-[#ABABAD]">
                <Iconify icon="mdi:file-document-outline" width={32} />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-[#D1D2D3] mb-2">Nenhuma proposta encontrada</h3>
              <p className="text-gray-500 dark:text-[#ABABAD] max-w-sm mb-6">
                Crie seu primeiro orçamento ou proposta comercial. Dá para vincular a uma oportunidade do CRM ou criar avulso.
              </p>
              <Button variant="link" className="text-[#7C3AED] font-medium" onClick={() => navigate(ROTA_NOVO_ORCAMENTO)}>
                Criar orçamento agora
              </Button>
            </div>
          </Card>
        ) : (
          <GenericTable headLabels={HEAD_LABELS}>
            {filteredPropostas.map((proposta) => (
              <div
                key={proposta._id}
                className={GENERIC_TABLE_DESIGN.row.container}
                onClick={() => navigate(caminhoEdicao(proposta))}
              >
                <div className={`${GENERIC_TABLE_DESIGN.common.colMain} flex items-center gap-2.5 cursor-pointer`}>
                  <Iconify icon={iconeTipo(proposta)} width={16} className="text-[#7C3AED] flex-shrink-0" />
                  <div className="min-w-0">
                    <span className="text-sm font-medium text-[#7C3AED] hover:underline block truncate">
                      {proposta.assinatura_cliente?.assinado_em && (
                        <Iconify
                          icon="mdi:draw-pen"
                          width={13}
                          title="Assinada pelo cliente"
                          className="inline-block mr-1 -mt-0.5 text-green-600 dark:text-green-400"
                        />
                      )}
                      {proposta.titulo}
                    </span>
                    <span className="flex items-center gap-1.5">
                      {proposta.numero && <span className="text-xs text-gray-400">#{proposta.numero}</span>}
                      {isOrcamento(proposta) && (
                        <span className="text-[10px] font-medium px-1.5 py-px rounded bg-gray-100 dark:bg-gray-500/10 text-gray-600 dark:text-gray-400">
                          Orçamento
                        </span>
                      )}
                    </span>
                  </div>
                </div>
                <div className="w-[140px]">{renderStatusBadge(proposta.status)}</div>
                <div className="w-[180px] text-sm text-gray-600 dark:text-[#ABABAD] truncate">
                  {proposta.lead_id?.nome || '-'}
                </div>
                <div className="w-[160px] text-sm text-gray-600 dark:text-[#ABABAD] truncate">
                  {proposta.vendedor_id?.nomeFantasia || proposta.vendedor_id?.nome || '-'}
                </div>
                <div className="w-[140px] text-right text-sm font-medium text-gray-900 dark:text-[#D1D2D3]">
                  {formatCurrency(proposta.valor_total)}
                </div>
                <div className="w-[140px] text-sm text-gray-500 dark:text-[#ABABAD]">
                  {formatDate(proposta.created_at)}
                </div>
                <div className="w-[80px] flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
                  <button
                    onClick={() => setAcompanhamentoTarget(proposta)}
                    title="Acompanhamento do cliente"
                    className="p-1.5 rounded-lg text-gray-400 hover:text-[#7C3AED] hover:bg-violet-50 dark:hover:bg-violet-500/10 transition-colors"
                  >
                    <Iconify icon="mdi:chart-timeline-variant" width={18} />
                  </button>
                  <button
                    onClick={() => setShareTarget(proposta)}
                    title="Compartilhar / baixar PDF"
                    className="p-1.5 rounded-lg text-gray-400 hover:text-[#7C3AED] hover:bg-violet-50 dark:hover:bg-violet-500/10 transition-colors"
                  >
                    <Iconify icon="mdi:share-variant-outline" width={18} />
                  </button>
                </div>
              </div>
            ))}
          </GenericTable>
        )}
        </>
        )}
      </div>

      <CompartilharPropostaDialog
        open={Boolean(shareTarget)}
        onClose={() => setShareTarget(null)}
        proposta={shareTarget}
        temChat={Boolean(shareTarget?.chat_id)}
      />

      {acompanhamentoTarget && (
        <AcompanhamentoDrawer
          proposta={acompanhamentoTarget}
          onClose={() => setAcompanhamentoTarget(null)}
        />
      )}
    </div>
  );
}
